// ────────────────────────────────────────────────────────────
// /mi/inbox — Monday weekly ingest workspace
// ────────────────────────────────────────────────────────────
// Two drop zones side by side. Recorder CSVs ingest sequentially
// (Cloudflare CPU rolling-average throttles parallel uploads of
// compute-heavy bodies — burned us during foundation load) and
// auto-trigger refresh-ingest-coverage after the last file in the
// batch. Pool order PDFs pair via filename hint (Application /
// Names) with manual slot fallback, then POST as multipart.
//
// Foundation mode is hidden behind an Advanced expander AND requires
// the operator to type the word FOUNDATION before any upload fires.
// 95% of operations are weekly; the typed confirmation is the
// safety against accidental "foundation_load=true on a weekly CSV"
// which silently excludes the new docs from this-week stats.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  inboxApi,
  type RecorderIngestResponse,
  type RefreshCoverageResponse,
  type InboxRecentRow,
  type PoolOrderIngestResponse,
} from '../services/inboxApi';

// ── Recorder upload queue types ────────────────────────────────────
type RecorderRowStatus = 'queued' | 'uploading' | 'success' | 'error';
interface RecorderRow {
  id: string;            // client-side uuid
  file: File;
  status: RecorderRowStatus;
  result?: RecorderIngestResponse;
  error?: string;
  startedAt?: number;
  finishedAt?: number;
}

export function Inbox() {
  // ── Recorder state ───────────────────────────────────────────────
  const [queue, setQueue] = useState<RecorderRow[]>([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  // Foundation mode: hidden behind Advanced expander and gated by
  // typed FOUNDATION confirmation. enabled+confirmed both true =>
  // X-Foundation-Load header sent on every recorder POST.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [foundationEnabled, setFoundationEnabled] = useState(false);
  const [foundationConfirmText, setFoundationConfirmText] = useState('');
  const foundationConfirmed = foundationEnabled && foundationConfirmText.trim().toUpperCase() === 'FOUNDATION';

  // Track whether a coverage refresh is in flight or has completed.
  const [refreshing, setRefreshing] = useState(false);
  const [coverageResult, setCoverageResult] = useState<RefreshCoverageResponse | null>(null);

  // ── Pool order state ─────────────────────────────────────────────
  const [appFile, setAppFile] = useState<File | null>(null);
  const [namesFile, setNamesFile] = useState<File | null>(null);
  const [docketOverride, setDocketOverride] = useState('');
  const [poUploading, setPoUploading] = useState(false);
  const [poError, setPoError] = useState<string | null>(null);
  const [poResult, setPoResult] = useState<PoolOrderIngestResponse | null>(null);

  // ── Recent ingests log ───────────────────────────────────────────
  const [recent, setRecent] = useState<InboxRecentRow[] | null>(null);
  const [recentError, setRecentError] = useState<string | null>(null);

  async function loadRecent() {
    try {
      const r = await inboxApi.recent();
      setRecent(r.recent);
      setRecentError(null);
    } catch (e: any) {
      setRecentError(e?.message ?? 'Failed to load recent ingests');
    }
  }

  useEffect(() => { loadRecent(); }, []);

  // ── Recorder upload pump ─────────────────────────────────────────
  // Single-flight: the effect kicks off the next 'queued' file when
  // there's no 'uploading' file. After a file completes, the effect
  // re-runs and either picks the next queued file or, if the whole
  // queue is now success/error and at least one succeeded, triggers
  // the auto coverage refresh.
  useEffect(() => {
    const uploading = queue.find(r => r.status === 'uploading');
    if (uploading) return;
    const next = queue.find(r => r.status === 'queued');
    if (next) {
      void runNext(next.id);
      return;
    }
    // Queue drained. If we ran at least one successful upload AND we
    // haven't already refreshed for this drain, kick the refresh.
    const anySuccess = queue.some(r => r.status === 'success');
    const allTerminal = queue.every(r => r.status === 'success' || r.status === 'error');
    if (anySuccess && allTerminal && !refreshing && coverageResult === null) {
      void triggerRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  async function runNext(rowId: string) {
    const row = queueRef.current.find(r => r.id === rowId);
    if (!row) return;

    // Foundation mode mid-batch toggle guard: if the operator enabled
    // foundation BUT hasn't typed the confirmation, we refuse to send.
    // The UI also disables the drop zone, but this is a belt-and-
    // suspenders check.
    if (foundationEnabled && !foundationConfirmed) {
      setQueue(q => q.map(r => r.id === rowId ? { ...r, status: 'error', error: 'Foundation mode requires typing FOUNDATION to confirm.' } : r));
      return;
    }

    setQueue(q => q.map(r => r.id === rowId ? { ...r, status: 'uploading', startedAt: Date.now() } : r));
    try {
      const result = await inboxApi.recorderIngest(row.file, foundationConfirmed);
      setQueue(q => q.map(r => r.id === rowId ? { ...r, status: 'success', result, finishedAt: Date.now() } : r));
    } catch (e: any) {
      setQueue(q => q.map(r => r.id === rowId ? { ...r, status: 'error', error: e?.message ?? 'Upload failed', finishedAt: Date.now() } : r));
    }
  }

  async function triggerRefresh() {
    setRefreshing(true);
    try {
      const r = await inboxApi.refreshCoverage();
      setCoverageResult(r);
      void loadRecent();
    } catch (e: any) {
      setCoverageResult(null);
      // surface as toast-style error at the top of the section
      alert(`Coverage refresh failed: ${e?.message ?? e}`);
    } finally {
      setRefreshing(false);
    }
  }

  function addCsvFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    const rows: RecorderRow[] = arr
      .filter(f => /\.csv$/i.test(f.name))
      .map(f => ({ id: cryptoRandom(), file: f, status: 'queued' as const }));
    if (rows.length === 0) return;
    setQueue(q => [...q, ...rows]);
    // New batch starting — clear the previous batch's coverage result
    // so the auto-refresh fires once this batch completes.
    setCoverageResult(null);
  }

  function clearRecorderQueue() {
    setQueue([]);
    setCoverageResult(null);
  }

  // ── Pool order drop / pairing logic ──────────────────────────────
  // Filename hints: pull plausible Application / Names / Interested
  // Parties tokens out of the basename. If both files come in at once
  // and hints clash (both look like Application), we still seat them
  // in slot order; the operator can manually swap via dropdown.
  function classifyPdf(name: string): 'application' | 'names' | 'unknown' {
    const lower = name.toLowerCase();
    if (/\b(application|app|order)\b/.test(lower)) return 'application';
    if (/\b(names|interested\s*part|parties)\b/.test(lower) || /-names\b/.test(lower)) return 'names';
    return 'unknown';
  }

  function addPdfFiles(files: FileList | File[]) {
    const arr = Array.from(files).filter(f => /\.pdf$/i.test(f.name));
    if (arr.length === 0) return;
    let a = appFile;
    let n = namesFile;
    for (const f of arr) {
      const kind = classifyPdf(f.name);
      if (kind === 'application' && !a) { a = f; continue; }
      if (kind === 'names' && !n)       { n = f; continue; }
      // Unknown / both slots full: drop into first empty slot.
      if (!a) { a = f; continue; }
      if (!n) { n = f; continue; }
      // Both slots full and another file: replace Application (most
      // likely the operator intends to swap, and we don't queue
      // pool-order ingests).
      a = f;
    }
    setAppFile(a);
    setNamesFile(n);
    setPoError(null);
    setPoResult(null);
  }

  async function uploadPoolOrder() {
    if (!appFile) { setPoError('Application PDF is required.'); return; }
    setPoUploading(true);
    setPoError(null);
    setPoResult(null);
    try {
      const fd = new FormData();
      fd.append('application', appFile);
      if (namesFile) fd.append('names', namesFile);
      if (docketOverride.trim()) fd.append('docket_number_override', docketOverride.trim());
      const r = await inboxApi.poolOrderIngest(fd);
      setPoResult(r);
      void loadRecent();
    } catch (e: any) {
      setPoError(e?.message ?? 'Upload failed');
    } finally {
      setPoUploading(false);
    }
  }

  function clearPoolOrder() {
    setAppFile(null);
    setNamesFile(null);
    setDocketOverride('');
    setPoError(null);
    setPoResult(null);
  }

  // ── Counts derived from queue ────────────────────────────────────
  const totals = useMemo(() => {
    const succeeded = queue.filter(r => r.status === 'success');
    const docs = succeeded.reduce((s, r) => s + (r.result?.summary.docs_created ?? 0), 0);
    const parties = succeeded.reduce((s, r) => s + (r.result?.summary.parties_created ?? 0), 0);
    return { docs, parties, succeeded: succeeded.length };
  }, [queue]);

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <header className="pb-5 border-b border-border">
        <div className="font-display text-[28px] leading-tight">Inbox</div>
        <div className="text-[12px] text-text-dim mt-1 font-mono">
          Drop weekly CSVs and pool order PDFs here. Recorder ingests auto-refresh ingest_coverage on completion.
        </div>
      </header>

      <div className="mt-6 grid grid-cols-2 gap-6">
        {/* ───────── Recorder CSV section ───────── */}
        <section className="bg-bg-card border border-border rounded p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display text-[16px]">Recorder CSV</div>
              <div className="text-[11px] text-text-faint font-mono mt-0.5">
                {foundationConfirmed
                  ? <span className="text-red">⚠ FOUNDATION MODE — docs excluded from this-week stats</span>
                  : <span>weekly ingest · docs count as new activity</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAdvancedOpen(o => !o)}
              className="text-[10px] font-mono px-2 py-1 rounded border border-border text-text-faint hover:text-text-dim"
            >
              {advancedOpen ? '▼ Advanced' : '▸ Advanced'}
            </button>
          </div>

          {advancedOpen && (
            <div className="mb-3 bg-bg-input/40 border border-border rounded p-3 space-y-2">
              <label className="flex items-start gap-2 text-[12px] text-text-dim">
                <input
                  type="checkbox"
                  className="mt-0.5 accent-accent"
                  checked={foundationEnabled}
                  onChange={e => {
                    setFoundationEnabled(e.target.checked);
                    if (!e.target.checked) setFoundationConfirmText('');
                  }}
                />
                <span>
                  <strong className="text-text">Foundation load</strong>
                  <span className="block text-[11px] text-text-faint font-mono mt-0.5">
                    One-time historical bulk import. Docs land with foundation_load=true and do NOT count as this-week activity. Wrong setting silently miscounts the brief and weekly pass.
                  </span>
                </span>
              </label>
              {foundationEnabled && (
                <div className="ml-6">
                  <div className="text-[11px] text-red mb-1 font-mono">
                    Type FOUNDATION below to enable foundation uploads:
                  </div>
                  <input
                    type="text"
                    value={foundationConfirmText}
                    onChange={e => setFoundationConfirmText(e.target.value)}
                    placeholder="FOUNDATION"
                    className={
                      'w-full px-2 py-1.5 text-[12px] font-mono bg-bg-input border rounded ' +
                      (foundationConfirmed
                        ? 'border-red text-red'
                        : 'border-border text-text')
                    }
                  />
                  <div className="text-[10px] text-text-faint font-mono mt-1">
                    {foundationConfirmed
                      ? 'Confirmed — uploads will use foundation_load=true.'
                      : 'Uploads disabled until confirmation typed.'}
                  </div>
                </div>
              )}
            </div>
          )}

          <DropZone
            accept=".csv,text/csv"
            multiple
            disabled={foundationEnabled && !foundationConfirmed}
            label="Drop CSVs here, or click to browse"
            onFiles={addCsvFiles}
          />

          {queue.length === 0 ? (
            <div className="mt-3 text-[11px] text-text-faint font-mono italic text-center py-3">
              No files queued
            </div>
          ) : (
            <ul className="mt-3 space-y-2">
              {queue.map(row => <RecorderQueueRow key={row.id} row={row} />)}
            </ul>
          )}

          <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-text-faint">
            <button
              type="button"
              onClick={clearRecorderQueue}
              disabled={queue.length === 0 || queue.some(r => r.status === 'uploading')}
              className="px-2 py-1 border border-border rounded hover:text-text hover:border-border-bright disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={triggerRefresh}
              disabled={refreshing}
              className="px-2 py-1 border border-border rounded hover:text-text hover:border-border-bright disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {refreshing ? 'Refreshing coverage…' : 'Refresh coverage'}
            </button>
            {totals.succeeded > 0 && (
              <span className="ml-auto text-text-dim">
                {totals.succeeded} file(s) · +{totals.docs} docs · +{totals.parties} parties
              </span>
            )}
          </div>

          {coverageResult && (
            <div className="mt-3 text-[11px] font-mono text-text-dim bg-accent-soft/30 border border-accent/30 rounded px-3 py-2">
              ✓ Coverage refreshed in {coverageResult.elapsed_ms}ms · {coverageResult.trs_count.toLocaleString()} TRSes · {coverageResult.earliest_recording_date} → {coverageResult.latest_recording_date}
            </div>
          )}
        </section>

        {/* ───────── Pool order PDF section ───────── */}
        <section className="bg-bg-card border border-border rounded p-5">
          <div className="mb-3">
            <div className="font-display text-[16px]">Pool order PDF</div>
            <div className="text-[11px] text-text-faint font-mono mt-0.5">
              Application + Names list (ECMC pooling dockets)
            </div>
          </div>

          <DropZone
            accept=".pdf,application/pdf"
            multiple
            disabled={false}
            label="Drop Application + Names PDFs here, or click to browse"
            onFiles={addPdfFiles}
          />

          <div className="mt-4 space-y-2">
            <PdfSlot label="Application" required file={appFile} onPick={f => setAppFile(f)} onClear={() => setAppFile(null)} />
            <PdfSlot label="Names list"  required={false} file={namesFile} onPick={f => setNamesFile(f)} onClear={() => setNamesFile(null)} />
          </div>

          <div className="mt-3">
            <label className="block text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1">
              Docket number override <span className="lowercase normal-case text-text-ghost">(optional, if Vision can't read it)</span>
            </label>
            <input
              type="text"
              value={docketOverride}
              onChange={e => setDocketOverride(e.target.value)}
              placeholder="e.g. 260400104"
              className="w-full px-2 py-1.5 text-[12px] font-mono bg-bg-input border border-border rounded"
            />
          </div>

          <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-text-faint">
            <button
              type="button"
              onClick={uploadPoolOrder}
              disabled={!appFile || poUploading}
              className="px-3 py-1.5 text-[12px] bg-accent text-bg rounded hover:bg-accent-bright disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {poUploading ? 'Uploading…' : 'Upload pair →'}
            </button>
            <button
              type="button"
              onClick={clearPoolOrder}
              disabled={poUploading || (!appFile && !namesFile && !docketOverride && !poError && !poResult)}
              className="px-2 py-1 border border-border rounded hover:text-text hover:border-border-bright disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>

          {poError && (
            <div className="mt-3 text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">
              {poError}
            </div>
          )}
          {poResult && (
            <div className={
              'mt-3 text-[12px] rounded px-3 py-2 border ' +
              (poResult.parse_status === 'complete'
                ? 'text-accent bg-accent-soft/30 border-accent/30'
                : 'text-amber bg-amber/10 border-amber/40')
            }>
              <div className="font-mono">
                {poResult.parse_status === 'complete' ? '✓' : '⚠'} {poResult.docket_number ? `ECMC ${poResult.docket_number}` : '(no docket)'} · parse {poResult.parse_status}
              </div>
              <Link to={`/mi/pool-orders/${poResult.pool_order_id}`} className="text-[11px] font-mono underline hover:no-underline mt-1 inline-block">
                Open pool order →
              </Link>
              {poResult.parse_warnings.length > 0 && (
                <ul className="mt-1 text-[11px] font-mono text-text-faint list-disc list-inside">
                  {poResult.parse_warnings.slice(0, 4).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ───────── Recent ingests ───────── */}
      <section className="mt-8">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="font-display text-[16px]">Recent ingests</div>
            <div className="text-[11px] text-text-faint font-mono mt-0.5">Last 10 csv batches + pool orders, newest first</div>
          </div>
          <button
            type="button"
            onClick={loadRecent}
            className="text-[10px] font-mono px-2 py-1 rounded border border-border text-text-faint hover:text-text-dim"
          >
            Refresh
          </button>
        </div>
        {recentError ? (
          <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">{recentError}</div>
        ) : recent === null ? (
          <div className="text-[11px] text-text-faint font-mono italic">loading…</div>
        ) : recent.length === 0 ? (
          <div className="text-[11px] text-text-faint font-mono italic">no ingests yet</div>
        ) : (
          <ul className="border border-border rounded divide-y divide-border bg-bg-card">
            {recent.map(r => <RecentRow key={r.kind + r.id} row={r} />)}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── DropZone ──────────────────────────────────────────────────────
// Native HTML5 drag-drop + click-to-browse. No deps. Disabled mode
// shows a greyed-out hover state (used when foundation mode is on but
// FOUNDATION hasn't been typed yet).
function DropZone({
  accept,
  multiple,
  disabled,
  label,
  onFiles,
}: {
  accept: string;
  multiple: boolean;
  disabled: boolean;
  label: string;
  onFiles: (files: FileList | File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setHover(false);
    if (disabled) return;
    if (e.dataTransfer?.files?.length) onFiles(e.dataTransfer.files);
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); if (!disabled) setHover(true); }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={
        'border-2 border-dashed rounded px-4 py-8 text-center cursor-pointer transition-colors ' +
        (disabled
          ? 'border-border bg-bg-input/30 opacity-50 cursor-not-allowed'
          : hover
            ? 'border-accent bg-accent-soft/30 text-text'
            : 'border-border bg-bg-input/40 text-text-faint hover:text-text-dim hover:border-border-bright')
      }
    >
      <div className="text-[12px] font-mono">{label}</div>
      {disabled && (
        <div className="text-[10px] font-mono text-red mt-1">disabled · type FOUNDATION to enable</div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}

// ── Recorder queue row ────────────────────────────────────────────
function RecorderQueueRow({ row }: { row: RecorderRow }) {
  const summary = row.result?.summary;
  return (
    <li className="border border-border rounded px-3 py-2 bg-bg-input/30">
      <div className="flex items-center gap-2">
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
        <span className="text-[12px] text-text truncate flex-1">{row.file.name}</span>
        <span className="text-[10px] text-text-faint font-mono">{formatBytes(row.file.size)}</span>
      </div>
      {row.status === 'success' && summary && (
        <div className="mt-1 text-[10px] font-mono text-text-dim">
          rows={summary.rows_processed} · +docs={summary.docs_created} =docs={summary.docs_already_present} · +parties={summary.parties_created} · +trs={summary.trs_links_created}
          {summary.unhandled_doc_types.length > 0 && (
            <span className="text-amber ml-2">⚠ {summary.unhandled_doc_types.length} unhandled doc_type(s)</span>
          )}
        </div>
      )}
      {row.status === 'success' && summary && summary.unhandled_doc_types.length > 0 && (
        <ul className="mt-1 text-[10px] font-mono text-text-faint list-disc list-inside">
          {summary.unhandled_doc_types.slice(0, 4).map(u => (
            <li key={u.doc_type}>{u.count}× {JSON.stringify(u.doc_type)}</li>
          ))}
          {summary.unhandled_doc_types.length > 4 && <li>… and {summary.unhandled_doc_types.length - 4} more</li>}
        </ul>
      )}
      {row.status === 'error' && (
        <div className="mt-1 text-[10px] font-mono text-red">{row.error}</div>
      )}
    </li>
  );
}

function statusBadgeClass(s: RecorderRowStatus): string {
  const base = 'text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ';
  switch (s) {
    case 'queued':    return base + 'bg-text-faint/15 text-text-faint';
    case 'uploading': return base + 'bg-accent/15 text-accent';
    case 'success':   return base + 'bg-green/15 text-green';
    case 'error':     return base + 'bg-red/15 text-red';
  }
}

// ── Pool order slot ────────────────────────────────────────────────
function PdfSlot({
  label, required, file, onPick, onClear,
}: {
  label: string;
  required: boolean;
  file: File | null;
  onPick: (f: File) => void;
  onClear: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <div className="w-[88px] font-mono text-[10px] uppercase tracking-wider text-text-faint">
        {label}{required && <span className="text-red"> *</span>}
      </div>
      {file ? (
        <>
          <span className="flex-1 truncate text-text">{file.name}</span>
          <span className="text-[10px] text-text-faint font-mono">{formatBytes(file.size)}</span>
          <button type="button" onClick={onClear} className="text-text-faint hover:text-red text-sm leading-none">×</button>
        </>
      ) : (
        <>
          <span className="flex-1 text-text-faint italic font-mono">{required ? '(required)' : '(optional)'}</span>
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className="px-2 py-1 text-[10px] font-mono border border-border rounded text-text-faint hover:text-text"
          >
            Browse…
          </button>
          <input
            ref={ref}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={e => { if (e.target.files?.[0]) onPick(e.target.files[0]); e.target.value = ''; }}
          />
        </>
      )}
    </div>
  );
}

// ── Recent row ────────────────────────────────────────────────────
function RecentRow({ row }: { row: InboxRecentRow }) {
  const ts = row.created_at.replace('T', ' ').slice(0, 19);
  const badgeColor =
    row.badge === 'foundation' ? 'text-amber bg-amber/15' :
    row.badge === 'weekly'     ? 'text-accent bg-accent/15' :
    row.badge === 'complete'   ? 'text-green bg-green/15' :
    row.badge === 'failed'     ? 'text-red bg-red/15' :
    'text-text-faint bg-text-faint/15';
  return (
    <li className="px-3 py-2 flex items-center gap-3">
      <span className="font-mono text-[10px] text-text-faint w-[140px] shrink-0">{ts}</span>
      <span className="font-mono text-[10px] uppercase tracking-wider w-[88px] text-text-dim shrink-0">{row.kind}</span>
      <span className={'font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ' + badgeColor}>{row.badge}</span>
      <span className="text-[12px] text-text truncate flex-1">{row.label}</span>
      <span className="text-[11px] font-mono text-text-faint truncate">{row.detail}</span>
      {row.link && (
        <Link to={row.link} className="text-[11px] font-mono text-accent hover:text-accent-bright shrink-0">open →</Link>
      )}
    </li>
  );
}

// ── Helpers ───────────────────────────────────────────────────────
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function cryptoRandom(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
